import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ErrorMessage, SuccessMessage } from 'components/mealplan/StatusMessages';
import { batchSave, logFrontendEvent, storePendingChanges, createCheckoutSession, fetchTaxRate, fetchCardFeeConfig, skipEmptyDates } from 'services/mealPlanService';
import { MEAL_TYPES } from 'constants/meals';
import { FALLBACK_TAX_RATE } from 'constants/pricing';
import { formatTaxRatePercent } from 'utils/tax';
import { formatDeliveryDate, formatWeekHeader } from 'utils/deliveryDates';
import { resolveDeliveryTime } from 'utils/deliveryTime';
import { withResumedDates } from 'utils/skippedDates';

/**
 * Shared content component for Order Summary
 * Used by both SummaryModal (mobile) and OrderSummarySidebar (desktop)
 *
 * @param {string} variant - 'modal' or 'sidebar' to adjust styling
 * @param {object} pendingChanges - Pending changes tracked per delivery date
 * @param {function} onSaveSuccess - Callback when save is successful
 */
const OrderSummaryContent = ({
    ordersByDeliveryDate,
    deliveryDates,
    billingChanges,
    customerToken,
    pendingChanges = {},
    priorPaymentsByDate = {},
    paymentRecords = {},
    onSaveSuccess,
    onDatesSkipped,
    variant = 'modal', // 'modal' or 'sidebar'
    customerDetails,
    deliveryTimeOverrides = {}
}) => {
    const subscriptions = withResumedDates(customerDetails?.subscriptions, ordersByDeliveryDate || {});
    const deliveryFees = customerDetails?.deliveryFees || {}; // keys: "zip | day | time" (lowercase)

    const getDeliveryTimeInfo = (date) => resolveDeliveryTime({
        deliveryFees,
        date,
        ordersForDate: ordersByDeliveryDate?.[date] || [],
        subscriptions,
        deliveryTimeOverrides
    });

    const [paymentLoading, setPaymentLoading] = useState({});
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [confirmationModal, setConfirmationModal] = useState({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
    // eslint-disable-next-line no-unused-vars
    const [successMessage, setSuccessMessage] = useState(''); // Used for save-only success feedback

    // Tax rate per zip code, looked up from the Delivery Fee table via /api/tax-rate/:zip
    const [taxRatesByZip, setTaxRatesByZip] = useState({});

    // No fee is shown until the backend's start date loads, since the server decides whether it's charged
    const [cardFeeConfig, setCardFeeConfig] = useState(null);
    useEffect(() => {
        fetchCardFeeConfig()
            .then(cfg => setCardFeeConfig({ rate: Number(cfg.rate) || 0, start: new Date(cfg.existingFeeStart).getTime() }))
            .catch(() => setCardFeeConfig(null));
    }, []);
    const CARD_FEE_RATE = cardFeeConfig?.rate || 0;
    const cardFeeActive = () => !!cardFeeConfig && Date.now() >= cardFeeConfig.start;

    useEffect(() => {
        const zips = new Set();
        (deliveryDates || []).forEach(date => {
            const zip = ordersByDeliveryDate?.[date]?.[0]?.zipCode;
            if (zip) zips.add(zip);
        });
        const zipsToFetch = [...zips].filter(zip => !(zip in taxRatesByZip));
        if (zipsToFetch.length === 0) return;

        zipsToFetch.forEach(zip => {
            fetchTaxRate(zip)
                .then(result => {
                    setTaxRatesByZip(prev => ({
                        ...prev,
                        [zip]: result?.supported ? result.taxRate : FALLBACK_TAX_RATE
                    }));
                })
                .catch(() => {
                    setTaxRatesByZip(prev => ({ ...prev, [zip]: FALLBACK_TAX_RATE }));
                });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryDates, ordersByDeliveryDate]);

    const getTaxRateForDate = (date) => {
        const zip = ordersByDeliveryDate?.[date]?.[0]?.zipCode;
        return zip && taxRatesByZip[zip] != null ? taxRatesByZip[zip] : FALLBACK_TAX_RATE;
    };

    // Define meal type order
    const mealTypeOrder = MEAL_TYPES.reduce((acc, mealType, index) => {
        acc[mealType] = index + 1;
        return acc;
    }, {});

    // Helper function for proper pluralization
    const pluralizeMealType = (mealType, count) => {
        if (count === 1) return mealType;
        if (mealType === 'Lunch') return 'Lunches';
        return `${mealType}s`;
    };

    // Check if any dirty date requires payment (positive amount due after prior payments)
    const WAIVER_THRESHOLD = 0.50; // Charges below this amount are waived

    // A date can owe (or be owed) money purely because a sibling date in the same
    // week consumed/freed up their shared meal pool — see PASS 2 in
    // calculateBillingChanges (MealPlanViewer.js) — even if that date itself has
    // no pending edits. Any "is there a charge/refund" check must look across the
    // whole affected week(s), not just the date(s) being saved, or a pool-shifted
    // amount on an untouched date goes undetected.
    const getWeekAffectedDates = (seedDates) => {
        const weekKeys = new Set(
            seedDates.map(d => billingChanges?.dates?.[d]?.weekKey).filter(Boolean)
        );
        if (!weekKeys.size) return seedDates;
        const expanded = new Set(seedDates);
        Object.keys(billingChanges?.dates || {}).forEach(d => {
            if (weekKeys.has(billingChanges.dates[d]?.weekKey)) expanded.add(d);
        });
        return Array.from(expanded);
    };

    const getDateTotal = (date) => {
        const net = billingChanges?.dates?.[date]?.net || 0;
        const fee = billingChanges?.dates?.[date]?.deliveryFee || 0;
        return net * (1 + getTaxRateForDate(date)) + fee;
    };

    const getAmountDue = (date) => getDateTotal(date) - (priorPaymentsByDate[date] || 0);

    const getWeekDates = (weekKey) => Object.keys(billingChanges?.dates || {})
        .filter(d => billingChanges.dates[d]?.weekKey === weekKey)
        .sort();

    // A refund on one date offsets charges on other dates in the same Sun–Sat (ET) week
    // rather than being charged in full and refunded manually later.
    // Returns { [date]: { gross, amountDue, creditsIn: [{date, amount}], creditsOut: [{date, amount}] } }.
    const getNettedAmounts = (seedDates) => {
        const toCents = (n) => Math.round(n * 100);
        const byWeek = {};
        getWeekAffectedDates(seedDates).forEach(date => {
            const weekKey = billingChanges?.dates?.[date]?.weekKey || date;
            (byWeek[weekKey] = byWeek[weekKey] || []).push(date);
        });

        const cents = {};
        Object.values(byWeek).forEach(weekDates => {
            const sorted = [...weekDates].sort();
            sorted.forEach(date => {
                const gross = toCents(getAmountDue(date));
                cents[date] = { gross, due: gross, creditsIn: [], creditsOut: [] };
            });
            const refunds = sorted.filter(d => cents[d].gross < 0).map(d => ({ date: d, left: -cents[d].gross }));
            sorted.filter(d => cents[d].gross > 0).forEach(date => {
                refunds.forEach(refund => {
                    const used = Math.min(refund.left, cents[date].due);
                    if (used <= 0) return;
                    refund.left -= used;
                    cents[date].due -= used;
                    cents[date].creditsIn.push({ date: refund.date, amount: used });
                    cents[refund.date].due += used;
                    cents[refund.date].creditsOut.push({ date, amount: used });
                });
            });
        });

        const toDollars = (list) => list.map(c => ({ date: c.date, amount: c.amount / 100 }));
        return Object.fromEntries(Object.entries(cents).map(([date, c]) => [date, {
            gross: c.gross / 100,
            amountDue: c.due / 100,
            creditsIn: toDollars(c.creditsIn),
            creditsOut: toDollars(c.creditsOut)
        }]));
    };

    // getAmountDue falls back to 0 whenever billingChanges (or its entry for a date)
    // isn't populated yet — indistinguishable from a genuinely free save. Money
    // decisions must confirm the data actually exists before trusting a $0 read.
    const isBillingDataReady = (dates) => {
        if (!billingChanges?.dates) return false;
        return getWeekAffectedDates(dates).every(date => billingChanges.dates[date] !== undefined);
    };

    const hasPaymentRequired = () => {
        const dirtyDates = displayDates.filter(date => pendingChanges[date]?.isDirty);
        return Object.values(getNettedAmounts(dirtyDates)).some(n => n.amountDue >= WAIVER_THRESHOLD);
    };

    const hasRefundRequired = () => {
        const dirtyDates = displayDates.filter(date => pendingChanges[date]?.isDirty);
        return Object.values(getNettedAmounts(dirtyDates)).some(n => n.amountDue < 0);
    };

    // Styling adjustments based on variant
    const isSidebar = variant === 'sidebar';
    const contentPadding = isSidebar ? '20px' : '24px';
    //console.log('dateChanges.changes', pendingChanges);

    // Show confirmation modal when payment/save button is clicked
    // Accepts either a single date string or an array of dates
    const handleButtonClick = (dateOrDates) => {
        const dates = Array.isArray(dateOrDates) ? dateOrDates : [dateOrDates];
        const hasPendingChanges = dates.some(date => pendingChanges[date]?.isDirty);
        const hasRefund = dates.some(date => (billingChanges?.dates?.[date]?.net || 0) < 0);

        logFrontendEvent('SAVE_BUTTON_CLICKED', customerToken, {
            dates,
            hasPendingChanges,
            hasRefund,
            pendingChangesSnapshot: dates.map(d => ({
                date: d,
                isDirty: pendingChanges[d]?.isDirty,
                changesCount: pendingChanges[d]?.changes?.length || 0
            }))
        });

        setConfirmationModal({ show: true, dates, isRefund: hasRefund, hasPendingChanges });
    };

    // Handle confirmed save — routes to Stripe Checkout if charges > 0, or saves directly
    const handleConfirmedPayment = async () => {
        const dates = confirmationModal.dates;

        // Refuse to save on incomplete pricing data rather than silently treating
        // "we don't know yet" as "$0 owed" — see isBillingDataReady above.
        if (!isBillingDataReady(dates)) {
            logFrontendEvent('SAVE_BLOCKED_BILLING_NOT_READY', customerToken, { dates });
            setErrorMessage("Still calculating your total — please try again in a few seconds.");
            return;
        }

        setCheckoutLoading(true);

        logFrontendEvent('SAVE_CONFIRMED', customerToken, {
            dates,
            changesPerDate: dates.map(d => ({
                date: d,
                isDirty: pendingChanges[d]?.isDirty,
                changesCount: pendingChanges[d]?.changes?.length || 0,
                changeTypes: pendingChanges[d]?.changes?.map(c => c.type) || []
            }))
        });

        // Set loading for all dates
        const loadingState = {};
        dates.forEach(date => { loadingState[date] = true; });
        setPaymentLoading(prev => ({ ...prev, ...loadingState }));

        try {
            const lineItems = [];
            const changesPerDate = {};

            // Expand beyond the dates actually being saved — a sibling date in the
            // same week can carry a pool-shifted charge (or refund) even though it
            // has no pending edits of its own (see getWeekAffectedDates above).
            const netted = getNettedAmounts(dates);
            for (const date of getWeekAffectedDates(dates)) {
                const isDirty = pendingChanges[date]?.isDirty && pendingChanges[date]?.changes?.length > 0;
                if (isDirty) {
                    changesPerDate[date] = { changes: pendingChanges[date].changes };
                }

                const dateData = billingChanges?.dates?.[date];
                const deliveryFee = dateData?.deliveryFee || 0;
                const priorPaid = priorPaymentsByDate[date] || 0;
                const { gross, amountDue, creditsIn } = netted[date];

                if (gross > 0) {
                    // Build detailed items list from billing data for Stripe display & logging
                    const items = [];
                    const meals = dateData?.meals || {};
                    Object.entries(meals).forEach(([mealType, mealData]) => {
                        const orderDetails = mealData?.orderDetails || [];
                        orderDetails.forEach(order => {
                            if (!order.isIncluded || (order.premiumProtein?.charge > 0)) {
                                items.push({
                                    name: order.itemName,
                                    quantity: order.quantity,
                                    mealType,
                                    price: order.isIncluded ? 0 : order.price * order.quantity,
                                    premiumProtein: order.premiumProtein?.charge > 0
                                        ? { name: order.premiumProtein.name, charge: order.premiumProtein.charge }
                                        : null
                                });
                            }
                        });
                    });

                    // Human-readable description shown on Stripe Checkout page
                    const descriptionParts = items.map(item => {
                        let desc = `${item.quantity}x ${item.name}`;
                        if (item.premiumProtein) desc += ` (Premium: ${item.premiumProtein.name})`;
                        return desc;
                    });

                    lineItems.push({
                        deliveryDate: date,
                        amount: parseFloat(amountDue.toFixed(2)),
                        grossAmount: parseFloat(gross.toFixed(2)),
                        sameWeekCredit: creditsIn,
                        description: descriptionParts.join(', ') || 'Meal plan adjustments',
                        items,
                        taxRate: getTaxRateForDate(date),
                        taxAmount: parseFloat(((dateData?.net || 0) * getTaxRateForDate(date)).toFixed(2)),
                        deliveryFee,
                        priorPaid,
                        zip: ordersByDeliveryDate?.[date]?.[0]?.zipCode
                    });

                    // This date may have no pending edits of its own — the charge came
                    // from a sibling date's pool shift — but it still needs an entry so
                    // the post-payment webhook has a delivery date to log the charge against.
                    if (!changesPerDate[date]) changesPerDate[date] = { changes: [] };
                }
            }

            const totalCharge = lineItems.reduce((sum, item) => sum + item.amount, 0);

            if (totalCharge > 0 && totalCharge < WAIVER_THRESHOLD) {
                // Small charge — waive it, save directly
                logFrontendEvent('CHARGE_WAIVED', customerToken, { dates, totalCharge, lineItems });
            } else if (totalCharge >= WAIVER_THRESHOLD) {
                // --- STRIPE CHECKOUT FLOW ---
                logFrontendEvent('STRIPE_CHECKOUT_STARTED', customerToken, {
                    dates,
                    totalCharge,
                    lineItems: lineItems
                });

                // 1. Store pending changes server-side
                const { pendingId } = await storePendingChanges(customerToken, changesPerDate, billingChanges);

                // 2. Create Stripe Checkout session
                const { url } = await createCheckoutSession(pendingId, lineItems, customerToken);

                // 3. Redirect to Stripe
                window.location.href = url;
                return; // Page will redirect — don't clear loading
            }

            // --- DIRECT SAVE FLOW (no charge or reductions only) ---
            setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
            setCheckoutLoading(false);
            const savedDates = [];
            const partialDates = [];
            const failedDates = [];
            const datesToSkip = dates.filter(date => billingChanges?.dates?.[date]?.skipsOnSave);

            for (const date of dates) {
                const hasDateChanges = pendingChanges[date]?.isDirty && pendingChanges[date]?.changes?.length > 0;
                if (!hasDateChanges) continue;

                try {
                    const response = await batchSave(customerToken, date, pendingChanges[date].changes, billingChanges);
                    const results = response.data.results || [];
                    const successfulResults = results.filter(r => r.success);

                    if (response.data.success) {
                        savedDates.push(date);
                        if (onSaveSuccess) onSaveSuccess(date, results, true);
                    } else if (successfulResults.length > 0) {
                        partialDates.push(date);
                        if (onSaveSuccess) onSaveSuccess(date, results, false);
                    } else {
                        failedDates.push(date);
                    }
                } catch (error) {
                    logFrontendEvent('SAVE_DATE_ERROR', customerToken, { date, error: error.message });
                    failedDates.push(date);
                }
            }

            const savedDatesToSkip = datesToSkip.filter(date => savedDates.includes(date) || partialDates.includes(date));
            if (savedDatesToSkip.length > 0) {
                try {
                    const { skipped = [] } = await skipEmptyDates(customerToken, savedDatesToSkip);
                    if (skipped.length && onDatesSkipped) onDatesSkipped(skipped);
                } catch (error) {
                    logFrontendEvent('SKIP_EMPTY_DATES_ERROR', customerToken, { error: error.message });
                }
            }

            if (savedDates.length > 0 && partialDates.length === 0 && failedDates.length === 0) {
                const waived = totalCharge > 0 && totalCharge < WAIVER_THRESHOLD;
                setSuccessMessage(waived
                    ? `All changes saved! Your $${totalCharge.toFixed(2)} balance was waived.`
                    : 'All changes saved successfully.');
                setTimeout(() => setSuccessMessage(''), 5000);
            } else if (partialDates.length > 0 || (savedDates.length > 0 && failedDates.length > 0)) {
                setErrorMessage('Changes partially saved. Please try again or refresh the page. If the issue persists, contact us.');
            } else if (failedDates.length > 0) {
                setErrorMessage('An error occurred while saving. Please try again or refresh the page. If the issue persists, contact us.');
            }
        } catch (error) {
            logFrontendEvent('SAVE_UNEXPECTED_ERROR', customerToken, {
                error: error.message,
                stack: error.stack
            });
            console.error('Save error:', error);
            setErrorMessage('An error occurred while saving. Please try again or refresh the page. If the issue persists, contact us.');
        } finally {
            setCheckoutLoading(false);
            setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
            const clearedLoading = {};
            dates.forEach(date => { clearedLoading[date] = false; });
            setPaymentLoading(prev => ({ ...prev, ...clearedLoading }));
        }
    };

    // Get dates that should be displayed
    const displayDates = (deliveryDates || []).filter(date => {
        const hasOrders = ordersByDeliveryDate?.[date]?.some(order => order.quantity > 0);
        const hasBillingChanges = billingChanges?.dates?.[date]?.hasChanges || billingChanges?.dates?.[date]?.isSkipped;
        const hasPendingChanges = pendingChanges[date]?.isDirty || false;
        return hasOrders || hasBillingChanges || hasPendingChanges;
    });

    // Group orders by meal type for a given date
    const getOrdersByMealType = (date) => {
        const ordersForDate = ordersByDeliveryDate[date] || [];
        const selectedOrders = ordersForDate.filter(order => order.quantity > 0);

        // Group by meal type
        const grouped = {};
        selectedOrders.forEach(order => {
            const mealType = order.meal || 'Other';
            if (!grouped[mealType]) {
                grouped[mealType] = [];
            }
            // Check if order already exists, if so add to quantity
            // Only consolidate if both have no premium protein (price is 0 or undefined/no protein options)
            const existingProteinPrice = order.proteinOptions?.find(opt => opt.isActive)?.priceSubscription || 0;
            const existing = grouped[mealType].find(o => {
                const oProteinPrice = o.proteinOptions?.find(opt => opt.isActive)?.priceSubscription || 0;
                return o.itemName === order.itemName && oProteinPrice === 0 && existingProteinPrice === 0;
            });
            if (existing) {
                existing.quantity += order.quantity;
            } else {
                grouped[mealType].push({ ...order });
            }
        });

        return { grouped };
    };

    return (
        <div style={{
            padding: contentPadding,
            backgroundColor: '#f5f0eb',
            minHeight: isSidebar ? '100vh' : 'auto'
        }}>
            {/* Header */}
            <div>
                <h2 style={{
                    color: '#333',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginTop: 0,
                    marginBottom: 0
                }}>
                    Order Summary
                </h2>
                {/* <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    ⚠️ Meal edits (except for skipping a delivery) are NOT saved until you complete the payment process or save changes.
                </p> */}
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    ⚠️ Any unsaved changes will be lost if you close/refresh this page.
                </p>
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    🧾 We will process any refunds within 5 business days after the delivery date.
                </p>

            </div>

            {/* Status Messages */}
            <ErrorMessage
                message={errorMessage}
                onClose={() => setErrorMessage('')}
            />
            <SuccessMessage
                message={successMessage}
                onClose={() => setSuccessMessage('')}
            />
            {(() => {
                const dirtyDates = displayDates.filter(date => pendingChanges[date]?.isDirty);
                const hasPendingChanges = dirtyDates.length > 0;
                const billingReady = !hasPendingChanges || isBillingDataReady(dirtyDates);
                const isDisabled = !hasPendingChanges || !billingReady || displayDates.some(date => paymentLoading[date]);

                return (
                    <button
                        onClick={() => handleButtonClick(dirtyDates)}
                        disabled={isDisabled}
                        style={{
                            width: '90%',
                            backgroundColor: isDisabled ? '#ccc' : '#ff7c44',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '18px 16px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                            transition: 'all 0.2s ease',
                            margin: '12px auto 36px',
                            display: 'block',
                        }}
                        onMouseOver={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#e66a2e';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#ff7c44';
                            }
                        }}
                    >
                        {displayDates.some(date => paymentLoading[date])
                            ? 'Processing...'
                            : !hasPendingChanges ? 'No Changes to Save'
                                : !billingReady ? 'Calculating Total…'
                                    : hasPaymentRequired() ? 'Checkout to Save Changes' : hasRefundRequired() ? 'Save Changes & Schedule Refunds' : 'Save Changes'
                        }
                    </button>
                );
            })()}

            {displayDates.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px',
                    padding: '40px 20px'
                }}>
                    No meals selected for any delivery date.
                </div>
            ) : (
                <div>

                    {displayDates.map((date) => {
                        const billingChangesDateData = billingChanges?.dates?.[date];
                        const { grouped } = getOrdersByMealType(date);

                        // Calculate net for this date
                        const netAmount = billingChangesDateData?.net || 0;

                        return (
                            <div key={date} style={{ marginBottom: '24px' }}>
                                {/* Date Header */}
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: '10px',
                                    fontWeight: '700',
                                    fontSize: '16px',
                                    color: '#333',
                                    marginBottom: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    <span>
                                        {formatDeliveryDate(date)} Delivery
                                        {!billingChangesDateData?.isSkipped && (() => {
                                            const { current, options } = getDeliveryTimeInfo(date);
                                            return options.length > 0 && current ? ` · ${current}` : null;
                                        })()}
                                    </span>
                                    {billingChangesDateData?.isSkipped && (
                                        <span style={{
                                            color: '#dc3545',
                                            fontWeight: '600',
                                            textTransform: 'none'
                                        }}>
                                            (Skipped)
                                        </span>
                                    )}
                                </div>

                                {/* Skipped Date - Simple meal type counts */}
                                {billingChangesDateData?.isSkipped ? (
                                    <div style={{ marginBottom: '12px' }}>
                                        {billingChangesDateData?.meals && Object.entries(billingChangesDateData.meals)
                                            .filter(([_, data]) => data.subscribed > 0)
                                            .sort(([a], [b]) => (mealTypeOrder[a] || 999) - (mealTypeOrder[b] || 999))
                                            .map(([mealType, data], idx, arr) => {
                                                const refundAmount = data.amount || 0;
                                                return (
                                                    <div
                                                        key={mealType}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 0',
                                                            fontSize: '14px',
                                                            color: '#333',
                                                            borderBottom: idx < arr.length - 1 ? '1px dashed #ddd' : 'none'
                                                        }}
                                                    >
                                                        <span>0 / {data.subscribed} {pluralizeMealType(mealType, data.subscribed)}</span>
                                                        {refundAmount < 0 && <span style={{
                                                            color: '#333',
                                                            fontStyle: 'normal',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            marginLeft: '8px',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            -${Math.abs(refundAmount).toFixed(2)}
                                                        </span>}
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                ) : (
                                    /* Active Date - Detailed dish listing */
                                    <div style={{ marginBottom: '12px' }}>
                                        {/* Show message if no meals selected */}
                                        {Object.keys(grouped).length === 0 && (
                                            <div style={{
                                                color: '#666',
                                                fontSize: '13px',
                                                fontStyle: 'italic',
                                                padding: '8px 0'
                                            }}>
                                                {billingChangesDateData?.skipsOnSave
                                                    ? 'All meals for this date are removed. This delivery will be skipped when you save.'
                                                    : pendingChanges[date]?.isDirty
                                                        ? 'All meals for this date are removed (unsaved change)'
                                                        : 'No meals selected for this date.'}
                                            </div>
                                        )}
                                        {/* Get meal types from both orders and billing changes */}
                                        {(() => {
                                            return MEAL_TYPES.map((mealType, mealIdx) => {
                                                const mealData = billingChangesDateData?.meals?.[mealType];
                                                const orderDetails = mealData?.orderDetails || [];
                                                const ordered = mealData?.ordered || 0;
                                                const subscribed = mealData?.subscribed || 0;
                                                const amount = mealData?.amount || 0;
                                                const prepaidRefunds = mealData?.prepaidRefunds || [];

                                                return (subscribed > 0 || ordered > 0 || prepaidRefunds.length > 0) && (
                                                    <div key={mealType} style={{ marginBottom: mealIdx < MEAL_TYPES.length - 1 ? '12px' : '0' }}>
                                                        {/* Meal Type Header - just shows count */}
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 0',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            color: '#333',
                                                            borderBottom: '1px dashed #ddd'
                                                        }}>
                                                            <span>
                                                                {subscribed > 0
                                                                    ? mealType === 'Snack'
                                                                        ? (() => {
                                                                            const days = subscribed / (customerDetails.customer.snacksPerDay || 1);
                                                                            return `${subscribed} ${pluralizeMealType(mealType, subscribed)} for ${days} ${days === 1 ? 'day' : 'days'} subscribed`;
                                                                        })()
                                                                        : `${ordered} ${pluralizeMealType(mealType, subscribed)}`
                                                                    : `${ordered} ${pluralizeMealType(mealType, ordered)}`}
                                                                {mealType === 'Snack' && (
                                                                    <span style={{
                                                                        fontSize: '12px',
                                                                        color: '#666',
                                                                        fontWeight: '400'
                                                                    }}>
                                                                        <br />(Recommended: {customerDetails.customer.snacksPerDay} snacks per day)
                                                                    </span>
                                                                )}
                                                            </span>

                                                            {amount < 0 && <span style={{
                                                                color: '#333',
                                                                fontStyle: 'normal',
                                                                fontSize: '13px',
                                                                fontWeight: '600',
                                                                marginLeft: '8px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                -${Math.abs(amount).toFixed(2)}
                                                            </span>}

                                                        </div>

                                                        {/* Individual Dishes with per-meal pricing */}
                                                        {orderDetails.map((orderDetail, orderIdx) => (
                                                            <div
                                                                key={`${orderDetail.recordId}-${orderIdx}`}
                                                                style={{
                                                                    padding: '8px 0 8px 0',
                                                                    fontSize: '13px',
                                                                    color: '#333',
                                                                    borderBottom: orderIdx < orderDetails.length - 1 ? '1px solid #eee' : 'none'
                                                                }}
                                                            >
                                                                {/* Meal name row */}
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'flex-start'
                                                                }}>
                                                                    <span style={{ flex: 1 }}>
                                                                        {orderDetail.itemName} {orderDetail.quantity > 1 ? `(×${orderDetail.quantity})` : ''}
                                                                    </span>
                                                                    <span style={{
                                                                        color: orderDetail.isIncluded ? '#666' : '#333',
                                                                        fontStyle: orderDetail.isIncluded ? 'italic' : 'normal',
                                                                        fontSize: orderDetail.isIncluded ? '12px' : '13px',
                                                                        fontWeight: orderDetail.isIncluded ? '400' : '600',
                                                                        marginLeft: '8px',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {orderDetail.isIncluded
                                                                            ? 'Included in plan'
                                                                            : `$${(orderDetail.price * orderDetail.quantity).toFixed(2)}`
                                                                        }
                                                                    </span>
                                                                </div>

                                                                {/* Premium protein row */}
                                                                {orderDetail.premiumProtein && (
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        paddingLeft: '12px',
                                                                        marginTop: '4px',
                                                                        fontSize: '12px',
                                                                        color: '#856404',
                                                                        fontStyle: 'italic'
                                                                    }}>
                                                                        <span>Premium Protein: {orderDetail.premiumProtein.name}</span>
                                                                        {orderDetail.premiumProtein.charge > 0 && (
                                                                            <span>+${orderDetail.premiumProtein.charge.toFixed(2)}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}

                                                        {prepaidRefunds.map(refund => (
                                                            <div
                                                                key={`prepaid-${refund.dishId}`}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    padding: '8px 0',
                                                                    fontSize: '13px',
                                                                    color: '#666',
                                                                    fontStyle: 'italic'
                                                                }}
                                                            >
                                                                <span style={{ flex: 1 }}>
                                                                    {refund.itemName} {refund.quantity > 1 ? `(×${refund.quantity})` : ''} — prepaid, not ordered
                                                                </span>
                                                                <span style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}>
                                                                    -${refund.amount.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                )}

                                {/* Subtotal, Tax, Delivery Fee, Total */}
                                {(() => {
                                    const deliveryFee = billingChangesDateData?.deliveryFee || 0;
                                    const hasChanges = netAmount !== 0 || deliveryFee !== 0;
                                    if (!hasChanges) return null;

                                    const taxRate = getTaxRateForDate(date);
                                    const taxAmount = netAmount * taxRate;
                                    const totalWithTax = netAmount * (1 + taxRate) + deliveryFee;

                                    return (
                                        <>
                                            {/* Subtotal */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 0',
                                                borderTop: '1px solid #ccc',
                                                fontSize: '13px'
                                            }}>
                                                <span>Subtotal</span>
                                                <span style={{ color: '#333' }}>
                                                    {netAmount > 0 ? `$${netAmount.toFixed(2)}` : netAmount < 0 ? `-$${Math.abs(netAmount).toFixed(2)}` : '$0.00'}
                                                </span>
                                            </div>

                                            {/* Sales Tax */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '4px 0',
                                                fontSize: '13px'
                                            }}>
                                                <span>Sales Tax ({formatTaxRatePercent(taxRate)}%)</span>
                                                <span style={{ color: '#333' }}>
                                                    {taxAmount >= 0 ? `$${taxAmount.toFixed(2)}` : `-$${Math.abs(taxAmount).toFixed(2)}`}
                                                </span>
                                            </div>

                                            {/* Delivery Fee */}
                                            {deliveryFee !== 0 && (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '4px 0',
                                                    fontSize: '13px'
                                                }}>
                                                    <span>Delivery Fee {(() => {
                                                        const zip = ordersByDeliveryDate?.[date]?.[0]?.zipCode;
                                                        return zip ? <span style={{ color: '#666', fontStyle: 'italic', fontSize: '12px', fontWeight: '400' }}>(Zip Code: {zip})</span> : null;
                                                    })()}</span>
                                                    <span style={{ color: '#333' }}>
                                                        {deliveryFee > 0 ? `$${deliveryFee.toFixed(2)}` : `-$${Math.abs(deliveryFee).toFixed(2)}`}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Total */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 0',
                                                borderTop: '1px solid #ccc',
                                                fontWeight: '600',
                                                fontSize: '14px'
                                            }}>
                                                <span>Total ({formatDeliveryDate(date)})</span>
                                                <span style={{ color: '#333' }}>
                                                    {totalWithTax > 0 ? `$${totalWithTax.toFixed(2)}` : totalWithTax < 0 ? `-$${Math.abs(totalWithTax).toFixed(2)}` : '$0.00'}
                                                </span>
                                            </div>

                                        </>
                                    );
                                })()}

                                {/* Save/Payment Button - show if has pending changes OR monetary changes
                                {(() => {
                                    const hasPendingChanges = pendingChanges[date]?.isDirty || false;
                                    const buttonText = `Save Changes for ${formatDeliveryDate(date)}`;
                                    const isDisabled = !hasPendingChanges || paymentLoading[date];
                                    if (!buttonText) return null;

                                    return (
                                        <button
                                            onClick={() => handleButtonClick(date)}
                                            disabled={isDisabled}
                                            style={{
                                                width: '80%',
                                                backgroundColor: isDisabled ? '#ccc' : 'rgb(255, 177, 60)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '20px',
                                                padding: '10px 16px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                                transition: 'all 0.2s ease',
                                                margin: '12px auto 0',
                                                display: 'block',

                                            }}
                                            onMouseOver={(e) => {
                                                if (!isDisabled) {
                                                    e.target.style.backgroundColor = '#e66a2e';
                                                }
                                            }}
                                            onMouseOut={(e) => {
                                                if (!isDisabled) {
                                                    e.target.style.backgroundColor = 'rgb(255, 177, 60)';
                                                }
                                            }}
                                        >
                                            {paymentLoading[date]
                                                ? 'Processing...'
                                                : hasPendingChanges
                                                    ? buttonText
                                                    : `No Changes to Save for ${formatDeliveryDate(date)}`
                                            }
                                        </button>
                                    );
                                })()} */}

                                {/* Weekly settlement — charges, refunds and payments net across the Sun–Sat week */}
                                {(() => {
                                    const weekKey = billingChangesDateData?.weekKey;
                                    if (!weekKey) return null;
                                    const isLastOfWeek = !displayDates.some(d => d > date && billingChanges?.dates?.[d]?.weekKey === weekKey);
                                    if (!isLastOfWeek) return null;

                                    const weekDates = getWeekDates(weekKey);
                                    const dateTotals = weekDates
                                        .map(d => ({ date: d, total: getDateTotal(d) }))
                                        .filter(d => Math.abs(d.total) > 0.005);
                                    const payments = weekDates.flatMap(d => {
                                        const records = paymentRecords[d] || [];
                                        if (records.length > 0) return records.map((r, i) => ({ key: `${d}-${i}`, amount: r.amount, receiptUrl: r.receiptUrl, timestamp: r.timestamp || '' }));
                                        const prior = priorPaymentsByDate[d] || 0;
                                        return prior > 0 ? [{ key: `${d}-prior`, amount: prior, timestamp: '' }] : [];
                                    }).sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)));
                                    const weekTotal = dateTotals.reduce((sum, d) => sum + d.total, 0);
                                    const paid = weekDates.reduce((sum, d) => sum + (priorPaymentsByDate[d] || 0), 0);
                                    const preFeeBalance = weekTotal - paid;
                                    const cardFee = preFeeBalance > 0.005 && cardFeeActive() ? preFeeBalance * CARD_FEE_RATE : 0;
                                    const balance = preFeeBalance + cardFee;
                                    if (dateTotals.length < 2 && payments.length === 0 && cardFee === 0) return null;
                                    const money = (n) => n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`;
                                    const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '13px' };

                                    return (
                                        <div style={{ borderTop: '2px solid #333', marginTop: '16px', paddingTop: '10px' }}>
                                            <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '6px' }}>
                                                {formatWeekHeader(weekKey)}
                                            </div>
                                            {dateTotals.length > 1 && dateTotals.map(d => (
                                                <div key={d.date} style={row}>
                                                    <span>{formatDeliveryDate(d.date)}</span>
                                                    <span>{money(d.total)}</span>
                                                </div>
                                            ))}
                                            <div style={{ ...row, fontWeight: '600' }}>
                                                <span>Week Total</span>
                                                <span>{money(weekTotal)}</span>
                                            </div>
                                            {payments.map((p, i) => (
                                                <div key={p.key} style={row}>
                                                    <span>
                                                        {payments.length > 1 ? `Payment Made ${i + 1}` : 'Payment Made'}
                                                        {p.receiptUrl && (
                                                            <a
                                                                href={p.receiptUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#ff7c44', fontSize: '12px', textDecoration: 'none', marginLeft: '8px' }}
                                                            >
                                                                View Receipt
                                                            </a>
                                                        )}
                                                    </span>
                                                    <span>{money(-p.amount)}</span>
                                                </div>
                                            ))}
                                            {cardFee > 0 && (
                                                <div style={row}>
                                                    <span>Card Processing Fee ({+(CARD_FEE_RATE * 100).toFixed(1)}%)</span>
                                                    <span>{money(cardFee)}</span>
                                                </div>
                                            )}
                                            <div style={{ ...row, borderTop: '1px solid #ccc', marginTop: '4px', paddingTop: '6px', fontWeight: '700', fontSize: '14px' }}>
                                                <span>{balance > 0.005 ? 'Balance Due' : balance < -0.005 ? 'Refund Due' : 'Settled'}</span>
                                                <span>{Math.abs(balance) > 0.005 ? money(balance) : '$0.00'}</span>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Separator line between dates */}
                                {displayDates.indexOf(date) < displayDates.length - 1 && (
                                    <div style={{
                                        borderBottom: '3px solid #ccc',
                                        marginTop: '24px',
                                        marginBottom: '24px'
                                    }} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {(() => {
                const dirtyDates = displayDates.filter(date => pendingChanges[date]?.isDirty);
                const hasPendingChanges = dirtyDates.length > 0;
                const billingReady = !hasPendingChanges || isBillingDataReady(dirtyDates);
                const isDisabled = !hasPendingChanges || !billingReady || displayDates.some(date => paymentLoading[date]);

                return (
                    <button
                        onClick={() => handleButtonClick(dirtyDates)}
                        disabled={isDisabled}
                        style={{
                            width: '90%',
                            backgroundColor: isDisabled ? '#ccc' : '#ff7c44',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '18px 16px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                            transition: 'all 0.2s ease',
                            margin: '12px auto 36px',
                            display: 'block',
                        }}
                        onMouseOver={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#e66a2e';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#ff7c44';
                            }
                        }}
                    >
                        {displayDates.some(date => paymentLoading[date])
                            ? 'Processing...'
                            : !hasPendingChanges ? 'No Changes to Save'
                                : !billingReady ? 'Calculating Total…'
                                    : hasPaymentRequired() ? 'Checkout to Save Changes' : hasRefundRequired() ? 'Save Changes & Schedule Refunds' : 'Save Changes'
                        }
                    </button>
                );
            })()}

            {/* Contact Info */}
            <p style={{
                color: '#888',
                textAlign: 'center',
                fontSize: '12px',
                marginTop: '20px',
                marginBottom: 0
            }}>
                Questions? Contact us at <a href="mailto:team@getsquarefare.com" style={{ color: '#ff7c44' }}>team@getsquarefare.com</a>
            </p>

            {/* Confirmation Modal - Using Portal to escape stacking context for Safari compatibility */}
            {confirmationModal.show && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
                        }
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '20px',
                            padding: '28px 24px',
                            maxWidth: '420px',
                            width: '90%',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const chargeDates = [];
                            const reductionDates = [];
                            const appliedCredits = [];

                            const netted = getNettedAmounts(confirmationModal.dates);
                            Object.entries(netted).sort(([a], [b]) => a.localeCompare(b)).forEach(([date, n]) => {
                                if (n.gross > 0.005) {
                                    chargeDates.push({ date, amountDue: n.gross });
                                    n.creditsIn.forEach(c => appliedCredits.push({ ...c, toDate: date }));
                                }
                                if (n.amountDue < -0.005) {
                                    reductionDates.push({ date, amountDue: n.amountDue });
                                }
                            });

                            const preSurchargeTotal = Object.values(netted).reduce((sum, n) => sum + Math.max(n.amountDue, 0), 0);
                            const surcharge = cardFeeActive() ? preSurchargeTotal * CARD_FEE_RATE : 0;
                            const totalCharge = preSurchargeTotal + surcharge;
                            const needsPayment = preSurchargeTotal > 0.005;

                            return (
                                <>
                                    {/* Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '50%',
                                            backgroundColor: needsPayment ? 'rgba(255, 124, 68, 0.15)' : 'rgba(40, 167, 69, 0.15)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '18px', flexShrink: 0
                                        }}>
                                            {needsPayment ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d4572a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="1" y="4" width="22" height="16" rx="2" />
                                                    <line x1="1" y1="10" x2="23" y2="10" />
                                                </svg>
                                            ) : '\u2705'}
                                        </div>
                                        <div>
                                            <div style={{ color: '#333', fontSize: '18px', fontWeight: '700' }}>
                                                {needsPayment ? 'Checkout to Save Changes' : hasRefundRequired() ? 'Schedule Refunds & Save Changes' : 'Save changes'}
                                            </div>
                                            {needsPayment && (
                                                <div style={{ color: '#888', fontSize: '13px', marginTop: '2px' }}>
                                                    You will be redirected to Stripe's secure payment page after clicking "Pay".
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Charge breakdown card */}
                                    {chargeDates.length > 0 && (
                                        <div style={{
                                            backgroundColor: '#f8f5f1',
                                            borderRadius: '12px',
                                            padding: '16px',
                                            margin: '16px 0 12px',
                                        }}>
                                            {chargeDates.map((d, i) => (
                                                <div key={d.date} style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    color: '#555', padding: '6px 0', fontSize: '14px',
                                                    borderBottom: i < chargeDates.length - 1 ? '1px solid #e8e3de' : 'none'
                                                }}>
                                                    <span>{formatDeliveryDate(d.date)} Meal Changes</span>
                                                    <span style={{ fontWeight: '600', color: '#333' }}>${d.amountDue.toFixed(2)}</span>
                                                </div>
                                            ))}
                                            {appliedCredits.map(c => (
                                                <div key={`${c.date}-${c.toDate}`} style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    color: '#555', padding: '6px 0', fontSize: '14px',
                                                }}>
                                                    <span>{formatDeliveryDate(c.date)} refund applied</span>
                                                    <span style={{ fontWeight: '600', color: '#28a745' }}>-${c.amount.toFixed(2)}</span>
                                                </div>
                                            ))}
                                            {needsPayment && cardFeeActive() && (
                                                <div style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    color: '#555', padding: '6px 0', fontSize: '14px',
                                                }}>
                                                    <span>Card Processing Fee ({+(CARD_FEE_RATE * 100).toFixed(1)}%)</span>
                                                    <span style={{ fontWeight: '600', color: '#333' }}>${surcharge.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {/* Total */}
                                            <div style={{
                                                display: 'flex', justifyContent: 'flex-end',
                                                borderTop: '1px solid #d5d0cb', marginTop: '8px', paddingTop: '10px',
                                            }}>
                                                <span style={{ fontSize: '22px', fontWeight: '700', color: '#c0562b' }}>
                                                    ${totalCharge.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Reduction info */}
                                    {reductionDates.length > 0 && (
                                        <div style={{
                                            backgroundColor: 'rgba(40, 167, 69, 0.08)',
                                            borderRadius: '12px',
                                            padding: '16px',
                                            margin: needsPayment ? '0 0 12px' : '16px 0 12px',
                                        }}>
                                            <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Refunds
                                                <br />[processed manually every Monday night]
                                            </div>
                                            {reductionDates.map(d => (
                                                <div key={d.date} style={{ display: 'flex', justifyContent: 'space-between', color: '#555', padding: '4px 0', fontSize: '14px' }}>
                                                    <span>{formatDeliveryDate(d.date)} delivery</span>
                                                    <span style={{ fontWeight: '600', color: '#28a745' }}>-${Math.abs(d.amountDue).toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* No-cost message */}
                                    {!needsPayment && (
                                        <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', margin: '16px 0', lineHeight: '1.5' }}>
                                            {confirmationModal.dates.length === 1
                                                ? <>Your edits for <b style={{ color: '#333' }}>{formatDeliveryDate(confirmationModal.dates[0])}</b> will be saved.</>
                                                : <>Your edits for <b style={{ color: '#333' }}>{confirmationModal.dates.map(d => formatDeliveryDate(d)).join(', ')}</b> will be saved.</>
                                            }
                                        </p>
                                    )}

                                    {/* Payment warning */}
                                    {needsPayment && (
                                        <div style={{
                                            backgroundColor: 'rgba(255, 124, 68, 0.08)',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            marginBottom: '16px',
                                            display: 'flex', alignItems: 'flex-start', gap: '10px',
                                        }}>
                                            <span style={{ fontSize: '16px', lineHeight: '1.3', flexShrink: 0 }}>&#9888;</span>
                                            <span style={{ color: '#666', fontSize: '13px', lineHeight: '1.5' }}>
                                                Changes are only saved after payment. If you cancel, your unsaved changes will be lost.
                                            </span>
                                        </div>
                                    )}

                                    {/* Buttons */}
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            onClick={() => setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false })}
                                            disabled={checkoutLoading}
                                            style={{
                                                flex: 1,
                                                padding: '14px 20px',
                                                backgroundColor: '#fff',
                                                color: checkoutLoading ? '#ccc' : '#666',
                                                border: '1px solid #ddd',
                                                borderRadius: '12px',
                                                fontSize: '15px',
                                                fontWeight: '600',
                                                cursor: checkoutLoading ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleConfirmedPayment}
                                            disabled={checkoutLoading}
                                            style={{
                                                flex: 1.2,
                                                padding: '14px 20px',
                                                backgroundColor: checkoutLoading ? '#ccc' : '#d4572a',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '12px',
                                                fontSize: '15px',
                                                fontWeight: '700',
                                                cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                            }}
                                        >
                                            {checkoutLoading ? 'Processing...' : (
                                                <>
                                                    {needsPayment && (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="1" y="4" width="22" height="16" rx="2" />
                                                            <line x1="1" y1="10" x2="23" y2="10" />
                                                        </svg>
                                                    )}
                                                    {needsPayment ? `Pay $${totalCharge.toFixed(2)}` : 'Save'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default OrderSummaryContent;
